import { useState, useCallback } from 'react'
import type { Square } from '../chessEngine'
import { getValidMoveSquares, applyMove } from '../chessEngine'
import type { ChessDifficulty, CapturePosition } from '../chessData'
import { generateCapturePosition } from '../chessData'
import type { SquareHighlight } from '../ChessBoard'
import ChessBoard from '../ChessBoard'
import { useTranslation } from '../../../i18n/LanguageContext'
import { useGameCompletion } from '../../../hooks/useGameCompletion'
import GamePlayHeader from '../../GamePlayHeader'
import SaveErrorBanner from '../../SaveErrorBanner'
import { COLORS } from '../../../styles/tokens'

interface Props {
  difficulty:  ChessDifficulty
  onCompleted: (stats: { moves: number; startTime: number | null; endTime: number | null; pairsTotal: number; pairsMatched: number }) => void
  onBack:      () => void
}

interface State {
  position:        CapturePosition
  fen:             string
  currentSquare:   Square
  remainingPawns:  Square[]
  selectedSquare:  Square | null
  validMoves:      Square[]
  moveCount:       number
  totalMoves:      number     // across all rounds
  round:           number
  answered:        number
  startTime:       number | null
  endTime:         number | null
  gameComplete:    boolean
  roundComplete:   boolean
  lastBest:        number | null
}

const ROUNDS_PER_GAME = 3

function initState(difficulty: ChessDifficulty): State {
  const position = generateCapturePosition(difficulty)
  return {
    position,
    fen:            position.fen,
    currentSquare:  position.controlSquare,
    remainingPawns: [...position.pawnSquares],
    selectedSquare: null,
    validMoves:     [],
    moveCount:      0,
    totalMoves:     0,
    round:          1,
    answered:       0,
    startTime:      null,
    endTime:        null,
    gameComplete:   false,
    roundComplete:  false,
    lastBest:       null,
  }
}

export default function CaptureMode({ difficulty, onCompleted, onBack }: Props) {
  const { t }   = useTranslation()
  const ch      = t.chess
  const [state, setState] = useState<State>(() => initState(difficulty))
  const [boardSize, setBoardSize] = useState(320)

  const handleBoardRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    const update = () => {
      const sz = Math.min(el.clientWidth, el.clientHeight, 480)
      setBoardSize(Math.floor(sz / 8) * 8)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
  }, [])

  const handleSquareClick = useCallback((sq: Square) => {
    setState((prev) => {
      if (prev.roundComplete || prev.gameComplete) return prev

      const startTime = prev.startTime ?? Date.now()

      // If a piece is selected and user clicks a valid move square
      if (prev.selectedSquare && prev.validMoves.includes(sq)) {
        const newFen = applyMove(prev.fen, prev.selectedSquare, sq)
        if (!newFen) return prev

        const newRemaining = prev.remainingPawns.filter((p) => p !== sq)
        const moveCount    = prev.moveCount + 1
        const roundDone    = newRemaining.length === 0

        return {
          ...prev,
          fen:            newFen,
          currentSquare:  sq,
          remainingPawns: newRemaining,
          selectedSquare: null,
          validMoves:     [],
          moveCount,
          totalMoves:     prev.totalMoves + 1,
          startTime,
          roundComplete:  roundDone,
          answered:       roundDone ? prev.answered + 1 : prev.answered,
        }
      }

      // Select the controlled piece
      if (sq === prev.currentSquare) {
        const validMoves = getValidMoveSquares(prev.fen, sq)
        return { ...prev, selectedSquare: sq, validMoves, startTime }
      }

      // Deselect
      return { ...prev, selectedSquare: null, validMoves: [] }
    })
  }, [])

  const nextRound = useCallback(() => {
    setState((prev) => {
      const nextRound = prev.round + 1
      if (nextRound > ROUNDS_PER_GAME) {
        return { ...prev, gameComplete: true, endTime: Date.now() }
      }
      const position = generateCapturePosition(difficulty)
      return {
        ...prev,
        position,
        fen:            position.fen,
        currentSquare:  position.controlSquare,
        remainingPawns: [...position.pawnSquares],
        selectedSquare: null,
        validMoves:     [],
        moveCount:      0,
        round:          nextRound,
        roundComplete:  false,
        lastBest:       prev.position.optimalMoves,
      }
    })
  }, [difficulty])

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  state.gameComplete,
    activitySlug:  'chess',
    difficulty,
    mode:          'capture',
    moves:         state.totalMoves,
    startTime:     state.startTime,
    endTime:       state.endTime,
    pairsTotal:    ROUNDS_PER_GAME,
    pairsMatched:  state.answered,
    onCompleted:   () => onCompleted({
      moves:        state.totalMoves,
      startTime:    state.startTime,
      endTime:      state.endTime,
      pairsTotal:   ROUNDS_PER_GAME,
      pairsMatched: state.answered,
    }),
  })

  // Highlights
  const highlights: Partial<Record<Square, SquareHighlight>> = {}
  if (state.selectedSquare) {
    highlights[state.selectedSquare] = 'selected'
    for (const sq of state.validMoves) {
      highlights[sq] = state.remainingPawns.includes(sq) ? 'capture' : 'valid-move'
    }
  } else if (!state.selectedSquare) {
    highlights[state.currentSquare] = 'target'
  }

  const isHard     = difficulty === 'hard'
  const optimal    = state.position.optimalMoves
  const progress   = ((state.round - 1) / ROUNDS_PER_GAME) * 100

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}

      <GamePlayHeader
        title={ch.title}
        subtitle={ch.modes.capture.label}
        stats={[
          { value: state.moveCount, label: ch.capture.movesLabel },
          { value: `${state.position.pawnSquares.length - state.remainingPawns.length}/${state.position.pawnSquares.length}`, label: ch.capture.capturedLabel },
        ]}
        progress={progress}
        progressNow={state.round - 1}
        progressMax={ROUNDS_PER_GAME}
        onBack={onBack}
        backLabel={ch.backToDashboard}
      />

      <div style={s.content}>
        {isHard && !state.roundComplete && (
          <p style={s.optimalBadge}>
            {ch.capture.optimalLabel.replace('{n}', String(optimal))}
          </p>
        )}

        <p style={s.instruction}>{ch.capture.instruction}</p>

        <div ref={handleBoardRef} style={s.boardWrap}>
          <ChessBoard
            fen={state.fen}
            size={boardSize}
            highlights={highlights}
            onSquareClick={state.roundComplete ? undefined : handleSquareClick}
          />
        </div>

        {state.roundComplete && (
          <div style={s.feedback}>
            <p style={s.feedbackMoves}>
              {ch.capture.yourMovesLabel}: <strong>{state.moveCount}</strong>
            </p>
            {!isHard && (
              <p style={s.feedbackOptimal}>
                {ch.capture.bestLabel}: {optimal}
              </p>
            )}
            <button style={{ ...s.btn, backgroundColor: COLORS.greenDark }} onClick={nextRound}>
              {state.round >= ROUNDS_PER_GAME ? ch.capture.finishBtn : ch.capture.nextBtn}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root:         { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: COLORS.bgPage },
  content:      { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', overflowY: 'auto' },
  instruction:  { fontSize: '1rem', color: '#333', fontStyle: 'italic', margin: 0, textAlign: 'center' },
  optimalBadge: { margin: 0, fontSize: '1rem', fontWeight: 700, color: '#8B4513', background: '#fdebd0', padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #f0a500' },
  boardWrap:    { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' },
  feedback:     { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' },
  feedbackMoves: { fontSize: '1.125rem', fontWeight: 600, margin: 0, color: '#333', textAlign: 'center' },
  feedbackOptimal: { fontSize: '1rem', color: '#666', margin: 0, textAlign: 'center' },
  btn: {
    color: '#fff', border: 'none', borderRadius: 12, padding: '0.875rem 2rem',
    fontSize: '1rem', fontWeight: 700, cursor: 'pointer', minHeight: 48,
  },
}
